import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity'
import { Action, createReducer, on } from '@ngrx/store'

import { GridSwitchEnum } from '@galabid/gb-dashboard/shared/feature'
import {
  clearSearchHistoryAction,
  getAddSearchHistoryAction,
} from 'libs/gb-dashboard/shared/data-access-search-history/src/lib/+state/search-history.actions'
import * as CategoriesActions from './categories.actions'
import { CategoriesEntity, categoriesEntityName } from './categories.models'

export const CATEGORIES_FEATURE_KEY = 'categories'

export interface CategoriesState extends EntityState<CategoriesEntity> {
  selectedId?: string | number
  loaded: boolean
  count: number
  searchHistory: string[]
  dataView: GridSwitchEnum
  editCreateLoaded: boolean
}

export interface CategoriesPartialState {
  readonly [CATEGORIES_FEATURE_KEY]: CategoriesState
}

export const categoriesAdapter: EntityAdapter<CategoriesEntity> = createEntityAdapter<CategoriesEntity>({
  selectId: (categories) => categories.uuid,
  sortComparer: (a: CategoriesEntity, b: CategoriesEntity) => (a.order < b.order ? 0 : 1),
})

export const initialCategoriesState: CategoriesState = categoriesAdapter.getInitialState({
  editCreateLoaded: true,
  loaded: false,
  count: 0,
  dataView: GridSwitchEnum.Table,
  searchHistory: [],
})

const reducer = createReducer(
  initialCategoriesState,
  on(CategoriesActions.loadCategories, (state) => ({
    ...state,
    loaded: false,
  })),
  on(CategoriesActions.createCategorySuccess, (state) => ({
    ...state,
    count: state.count + 1,
  })),
  on(CategoriesActions.deleteCategorySuccess, (state) => ({
    ...state,
    count: state.count - 1,
  })),
  on(CategoriesActions.setCategories, (state, action) =>
    categoriesAdapter.setAll(action.categories, { ...state, loaded: true, count: action.count }),
  ),
  on(CategoriesActions.loadCategoriesSuccess, (state, action) =>
    categoriesAdapter.upsertMany(action.categories, { ...state, loaded: true, count: action.count }),
  ),
  on(CategoriesActions.loadCategoriesSuccess, (state, action) => ({
    ...state,
    count: action.count,
  })),
  on(CategoriesActions.updateCategoriesOrder, (state) => ({
    ...state,
    loaded: false,
  })),
  on(CategoriesActions.updateCategoriesSuccess, (state, { categories }) =>
    categoriesAdapter.updateMany(categories, { ...state, loaded: true }),
  ),
  on(CategoriesActions.clearSelectedCategory, (state) => ({
    ...state,
    selectedId: initialCategoriesState.selectedId,
  })),
  on(CategoriesActions.deleteCategorySuccess, (state, { uuid }) => categoriesAdapter.removeOne(uuid, state)),
  on(CategoriesActions.createCategorySuccess, (state, { category }) => categoriesAdapter.addOne(category, state)),
  on(CategoriesActions.updateCategorySuccess, (state, { category }) => categoriesAdapter.updateOne(category, state)),
  on(getAddSearchHistoryAction(categoriesEntityName), (state, action) => ({
    ...state,
    searchHistory: [...state.searchHistory, action.searchTerm],
  })),
  on(clearSearchHistoryAction(categoriesEntityName), (state, action) => ({
    ...state,
    searchHistory: initialCategoriesState.searchHistory,
  })),
  on(CategoriesActions.selectCategory, (state, action) => ({
    ...state,
    selectedId: action.uuid,
  })),
  on(CategoriesActions.createCategory, CategoriesActions.updateCategory, (state, action) => ({
    ...state,
    editCreateLoaded: false,
  })),
  on(
    CategoriesActions.createCategorySuccess,
    CategoriesActions.createCategoryFailure,
    CategoriesActions.updateCategorySuccess,
    CategoriesActions.updateCategoryFailure,
    (state, action) => ({
      ...state,
      editCreateLoaded: true,
    }),
  ),
  on(CategoriesActions.displayGrid, (state) => ({
    ...state,
    dataView: GridSwitchEnum.Grid,
  })),
  on(CategoriesActions.displayTable, (state) => ({
    ...state,
    dataView: GridSwitchEnum.Table,
  })),
  on(CategoriesActions.clearCategories, (state) => ({
    ...initialCategoriesState,
    dataView: state.dataView,
  })),
)

export function categoriesReducer(state: CategoriesState | undefined, action: Action) {
  return reducer(state, action)
}
